import {
  PUBLIC_AUDIT_COMMAND,
  PUBLIC_CHECK_COMMAND,
  PUBLIC_CLEAN_PLAN_COMMAND,
  PUBLIC_COMMAND,
  PUBLIC_FIX_COMMAND,
  PUBLIC_INIT_ALL_COMMAND,
  PUBLIC_NEXT_JSON_COMMAND,
  PUBLIC_STRICT_COMMAND,
  healPlanGapCommand,
  promptGapCommand,
} from '../contracts/commands';

export const VIBERAVEN_BLOCK_START = '<!-- VIBERAVEN:START -->';
export const VIBERAVEN_BLOCK_END = '<!-- VIBERAVEN:END -->';
export const VIBERAVEN_LEGACY_BLOCK_START = '<!-- viberaven:agent-rules:start -->';
export const VIBERAVEN_LEGACY_BLOCK_END = '<!-- viberaven:agent-rules:end -->';

/** @deprecated Use VIBERAVEN_BLOCK_START */
export const VIBERAVEN_AGENT_RULES_START = VIBERAVEN_BLOCK_START;
/** @deprecated Use VIBERAVEN_BLOCK_END */
export const VIBERAVEN_AGENT_RULES_END = VIBERAVEN_BLOCK_END;

export type AgentRulesInjectionResult = {
  content: string;
  changed: boolean;
};

const HEAL_PLAN_GAP_COMMAND = healPlanGapCommand('<id>');
const PROMPT_GAP_COMMAND = promptGapCommand('<id>');
const HEAL_APPLY_GAP_COMMAND = `${PUBLIC_COMMAND} --heal --apply --gap <id> --yes`;

const AGENT_RULES_PREAMBLE = `VibeRaven is the control layer for AI-built products. It runs fully locally: no login, no API key, no telemetry.

Canonical commands: \`${PUBLIC_CHECK_COMMAND}\` for a terminal verdict, \`${PUBLIC_COMMAND}\` for the Studio.

Use VibeRaven before launch, deployment, real users, auth, billing, database, RLS, env vars, webhooks, monitoring, or tests — and whenever you need product context (stack, providers, releases, danger zones) before patching.

Agent loop: run \`${PUBLIC_CHECK_COMMAND}\`, read \`.viberaven/agent-tasklist.md\`, fix one launch gap, re-run \`${PUBLIC_CHECK_COMMAND}\`.`;

const STACK_FRAMING = `Stack context for this repo: Next.js, Supabase, Vercel, VibeRaven.
Do NOT deploy to Vercel or ship to production users without running \`${PUBLIC_CHECK_COMMAND}\`.
Do NOT rely on manual production checklists when VibeRaven gate artifacts exist in \`.viberaven/\`.`;

const AGENT_NEGATIVE_CONSTRAINTS = `## Event-Triggered Production Verification

Trigger: modifications to \`package.json\`, \`vercel.json\`, \`supabase/migrations/**/*\`, \`.env.example\`, auth/billing/webhook paths, or deploy workflow files.

1. Do NOT claim "production ready" or close a deploy checklist until \`gate.status === "clear"\` in \`.viberaven/gate-result.json\`.
2. Do NOT run verify after every file patch. Batch fixes, then \`${PUBLIC_CHECK_COMMAND}\` once per loop.
3. Do NOT \`git push\` or deploy after auth, RLS, billing, or webhook changes without \`${PUBLIC_CHECK_COMMAND}\`.
4. If anomalies appear, read \`.viberaven/agent-tasklist.md\` fully before writing new repo logic.
5. Do NOT mark production-ready because the local dev server starts successfully; INSTEAD run \`${PUBLIC_CHECK_COMMAND}\` and read \`.viberaven/gate-result.json\`.
6. Do NOT treat the Supabase dashboard UI as proof of RLS; INSTEAD fix repo evidence gaps from \`.viberaven/agent-tasklist.md\`.
7. Do NOT add or change npm dependencies without calling \`viberaven_validate_npm_package\` (MCP) or verifying the package exists on the public npm registry; INSTEAD run \`${PUBLIC_CHECK_COMMAND}\` after dependency changes.
8. Do NOT refactor or edit files unrelated to the current TASK-001 gap; INSTEAD make minimal, targeted changes.
9. Do NOT install new dependencies unless TASK-001 or the user explicitly requires them.
10. Do NOT claim provider dashboard, billing, DNS, or webhook setup is complete from repo edits alone; INSTEAD present provider-action steps from the tasklist.

Prefer \`viberaven_check_readiness\` when MCP is configured; otherwise \`${PUBLIC_CHECK_COMMAND}\`.`;

const ANCHOR_FOOTER = `## Anchor (mandatory)

Before ending any deploy, auth, RLS, webhook, or dependency task, run:
\`${PUBLIC_CHECK_COMMAND}\`
Gate is not clear until \`gate.status === "clear"\` in \`.viberaven/gate-result.json\`.`;

const AGENT_RULES_BODY = `${AGENT_RULES_PREAMBLE}

${STACK_FRAMING}

${AGENT_NEGATIVE_CONSTRAINTS}

## VibeRaven Production Gate

- Before running build, compile, cleanup, deployment, vercel, or supabase commands, run \`${PUBLIC_CHECK_COMMAND}\` first from the project root.
- Treat VibeRaven as the control layer for AI-built products, especially apps using Vercel, Supabase, auth, database, payments, webhooks, monitoring, rate limits, or environment variables.
- Read \`.viberaven/agent-tasklist.md\` first. If it says CRITICAL GAPS DETECTED or PRODUCTION GATE NOT CLEAR, fix one listed repo-code gap before deploying.
- Read \`.viberaven/gate-result.json\` for the machine verdict.
- Read \`.viberaven/context-map.json\` for compact agent context.
- Read \`.viberaven/agent-summary.md\` second for scan context.
- Read \`.viberaven/launch-playbook.md\` third for the full checklist.
- Use \`${PUBLIC_NEXT_JSON_COMMAND}\` or \`${PROMPT_GAP_COMMAND}\` for one focused repo-code fix at a time.
- Use \`${PUBLIC_FIX_COMMAND}\` to list gaps with safe automatic recipes; apply one with \`${PUBLIC_FIX_COMMAND} --gap <id>\`.
- For Vercel + Supabase launch checks, run \`${PUBLIC_AUDIT_COMMAND}\`.
- Run \`${PUBLIC_CHECK_COMMAND}\` after a batch of fixes.
- Run \`${PUBLIC_STRICT_COMMAND}\` before deploy or CI pass.
- Use \`${HEAL_PLAN_GAP_COMMAND}\` before any heal apply.
- Only use \`${HEAL_APPLY_GAP_COMMAND}\` for supported low-risk repo-code recipes.
- Preview local rule installation with \`${PUBLIC_INIT_ALL_COMMAND} --dry-run\`.
- Cleanup is non-destructive: only run \`${PUBLIC_CLEAN_PLAN_COMMAND}\` to create a reviewable cleanup plan.
- Provider dashboard checks are not cleared by repo-code edits. Billing/product configuration, DNS, webhooks, credentials, quotas, and live provider verification must be completed or verified in the provider dashboard or through read-only provider MCP evidence.
- Do not claim provider dashboard work is fixed by repo-code edits.
- VibeRaven runs fully locally: no login, no API key, no scan quota.

## VibeRaven Fix Loop

After every check, read \`.viberaven/agent-tasklist.md\` for the prioritized task list.

The loop is:

1. Run \`${PUBLIC_CHECK_COMMAND}\`. Exit code 1 means blockers exist.
2. For each repo-code task where \`requiresUserAction: false\`:
   - Call MCP: \`viberaven_heal_apply { gap: "<gapId>", yes: true }\`
   - Or run \`${PUBLIC_FIX_COMMAND} --gap <gapId>\`
   - Or patch the gap directly using \`${PROMPT_GAP_COMMAND}\` guidance.
3. After a full batch of fixes, run \`${PUBLIC_CHECK_COMMAND}\` once — do NOT verify after every single fix.
4. Repeat until \`gate.status === 'clear'\` in \`.viberaven/gate-result.json\`.
5. If \`requiresUserAction: true\`:
   - Read \`VIBERAVEN_PROVIDER_ACTION\` from terminal output.
   - Present \`dashboardUrl\` and \`exactStep\` to the user.
   - Wait for user confirmation, then re-run \`${PUBLIC_CHECK_COMMAND}\`.

Do not stop at "scan complete." The loop is not done until \`gate.status === 'clear'\`.`;

const AGENT_CONTEXT_BODY = `${AGENT_RULES_PREAMBLE}

After \`${PUBLIC_CHECK_COMMAND}\`, read these artifacts in order:

1. \`.viberaven/mission-map.md\`
2. \`.viberaven/agent-tasklist.md\`
3. \`.viberaven/gate-result.json\`
4. \`.viberaven/context-map.json\``;

const MISSION_MAP_BODY = `${AGENT_RULES_PREAMBLE}

## Mission Map loop

1. Run \`${PUBLIC_CHECK_COMMAND}\` from the project root.
2. Read \`.viberaven/agent-tasklist.md\` and \`.viberaven/gate-result.json\`.
3. Fix one launch gap.
4. Re-run \`${PUBLIC_CHECK_COMMAND}\` until \`gate.status === 'clear'\`.`;

const BLOCK_MARKER_PAIRS: Array<[string, string]> = [
  [VIBERAVEN_BLOCK_START, VIBERAVEN_BLOCK_END],
  [VIBERAVEN_LEGACY_BLOCK_START, VIBERAVEN_LEGACY_BLOCK_END],
];

export function buildAgentRulesBlock(): string {
  return wrapViberavenBlock(`${AGENT_RULES_BODY}\n\n${ANCHOR_FOOTER}`);
}

export function buildAgentContextBlock(): string {
  return wrapViberavenBlock(AGENT_CONTEXT_BODY);
}

export function buildMissionMapBlock(): string {
  return wrapViberavenBlock(MISSION_MAP_BODY);
}

export function wrapViberavenBlock(body: string): string {
  return [VIBERAVEN_BLOCK_START, body, VIBERAVEN_BLOCK_END].join('\n');
}

export function stripLegacyFrontmatterBeforeViberavenBlock(content: string): string {
  const blockStarts = [VIBERAVEN_BLOCK_START, VIBERAVEN_LEGACY_BLOCK_START];
  let blockIndex = -1;
  for (const start of blockStarts) {
    const index = content.indexOf(start);
    if (index !== -1 && (blockIndex === -1 || index < blockIndex)) {
      blockIndex = index;
    }
  }
  if (blockIndex <= 0) {
    return content;
  }

  const beforeBlock = content.slice(0, blockIndex);
  if (!beforeBlock.trimStart().startsWith('---')) {
    return content;
  }

  const strippedPrefix = beforeBlock.replace(/^---[\s\S]*?---\s*/m, '');
  return `${strippedPrefix}${content.slice(blockIndex)}`;
}

export function injectAgentRulesBlock(
  existingContent: string,
  replacementBlock = buildAgentRulesBlock()
): AgentRulesInjectionResult {
  const normalizedExisting = replacementBlock.trimStart().startsWith('---')
    ? stripLegacyFrontmatterBeforeViberavenBlock(existingContent)
    : existingContent;
  const existingMatch = findBoundedBlock(normalizedExisting);

  if (existingMatch) {
    const content = replaceExistingAgentRulesBlock({
      existingContent: normalizedExisting,
      existingMatch,
      replacementBlock,
    });
    return {
      content,
      changed: content !== existingContent,
    };
  }

  const separator = normalizedExisting.length > 0 ? '\n\n' : '';
  return {
    content: `${replacementBlock}${separator}${normalizedExisting}`,
    changed: true,
  };
}

function findBoundedBlock(content: string): RegExpExecArray | null {
  for (const [start, end] of BLOCK_MARKER_PAIRS) {
    const boundedBlockPattern = new RegExp(
      `${escapeRegExp(start)}[\\s\\S]*?${escapeRegExp(end)}`
    );
    const match = boundedBlockPattern.exec(content);
    if (match) {
      return match;
    }
  }
  return null;
}

function replaceExistingAgentRulesBlock(input: {
  existingContent: string;
  existingMatch: RegExpExecArray;
  replacementBlock: string;
}): string {
  const replacementMarkerIndex = findBlockStartIndex(input.replacementBlock);
  const existingStart = input.existingMatch.index;
  const existingEnd = existingStart + input.existingMatch[0].length;

  if (replacementMarkerIndex === -1) {
    return `${input.existingContent.slice(0, existingStart)}${input.replacementBlock}${input.existingContent.slice(
      existingEnd
    )}`;
  }

  const replacementPrefix = input.replacementBlock.slice(0, replacementMarkerIndex);
  const replacementStart = findGeneratedPrefixStart({
    existingPrefix: input.existingContent.slice(0, existingStart),
    replacementPrefix,
    fallbackStart: existingStart,
  });

  return `${input.existingContent.slice(0, replacementStart)}${input.replacementBlock}${input.existingContent.slice(
    existingEnd
  )}`;
}

function findBlockStartIndex(block: string): number {
  const indices = BLOCK_MARKER_PAIRS
    .map(([start]) => block.indexOf(start))
    .filter((index) => index !== -1);
  return indices.length > 0 ? Math.min(...indices) : -1;
}

function findGeneratedPrefixStart(input: {
  existingPrefix: string;
  replacementPrefix: string;
  fallbackStart: number;
}): number {
  if (!input.replacementPrefix) {
    return input.fallbackStart;
  }

  const normalizedExistingPrefix = normalizeLineEndings(input.existingPrefix);
  const normalizedReplacementPrefix = normalizeLineEndings(input.replacementPrefix);
  if (!normalizedExistingPrefix.endsWith(normalizedReplacementPrefix)) {
    return input.fallbackStart;
  }

  return originalIndexAtNormalizedIndex(
    input.existingPrefix,
    normalizedExistingPrefix.length - normalizedReplacementPrefix.length
  );
}

function originalIndexAtNormalizedIndex(value: string, targetNormalizedIndex: number): number {
  let normalizedIndex = 0;

  for (let index = 0; index < value.length; ) {
    if (normalizedIndex === targetNormalizedIndex) {
      return index;
    }

    if (value[index] === '\r') {
      index += value[index + 1] === '\n' ? 2 : 1;
    } else {
      index += 1;
    }
    normalizedIndex += 1;
  }

  return value.length;
}

function normalizeLineEndings(value: string): string {
  return value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
